import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Distinguish browser back/forward from active navigation:
 * - Back/forward: prefer using cache for fast page restoration
 * - Active navigation: invalidate related cache and refetch fresh data
 */
const NavigationHandler = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const isPopStateNavigation = useRef(false);
  const previousPath = useRef(location.pathname);
  const previousLocationKey = useRef(location.key);

  // Listen to popstate events (browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      isPopStateNavigation.current = true;
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Listen to path changes
  useEffect(() => {
    const resetHomeState = () => {
      try {
        sessionStorage.removeItem('home_filter_state');
        const recommendationKeys = [];
        for (let i = 0; i < sessionStorage.length; i += 1) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith('recommendation_cards_')) {
            recommendationKeys.push(key);
          }
        }
        recommendationKeys.forEach((key) => sessionStorage.removeItem(key));
      } catch (error) {
        // Silently handle error
      }
    };

    const resetBrandState = (brandId) => {
      if (!brandId) return;
      try {
        sessionStorage.removeItem(`brand_page_filters_${brandId}`);
      } catch (error) {
        // Silently handle error
      }
    };

    const resetListingsState = () => {
      try {
        sessionStorage.removeItem('listings_page_state');
      } catch (error) {
        // Silently handle error
      }
    };

    const resetFavoritesState = () => {
      try {
        sessionStorage.removeItem('favorites_page_state');
      } catch (error) {
        // Silently handle error
      }
    };

    const resetOrdersState = () => {
      try {
        // Clear all orders page state keys
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i += 1) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith('orders_page_state')) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => sessionStorage.removeItem(key));

        // Also clear scroll position cache for orders page to avoid jumping back to an old anchor
        const scrollKeysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i += 1) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith('scroll_/me/orders')) {
            scrollKeysToRemove.push(key);
          }
        }
        scrollKeysToRemove.forEach((key) => sessionStorage.removeItem(key));
      } catch (error) {
        // Silently handle error
      }
    };

    const handleDocumentClick = (e) => {
      if (e.defaultPrevented) return;
      let target = e.target;
      while (target && target !== document) {
        if (target.tagName === 'A' && target.href) {
          try {
            const url = new URL(target.href);
            if (url.origin !== window.location.origin) {
              break;
            }
            if (url.pathname === '/') {
              resetHomeState();
            } else if (url.pathname === '/me/listings') {
              resetListingsState();
            } else if (url.pathname === '/me/favorites') {
              // When actively clicking into the favorites page, reset its pagination/scroll cache
              resetFavoritesState();
            } else if (url.pathname === '/me/orders') {
              // When actively clicking into the orders page, reset its pagination/scroll cache
              resetOrdersState();
            } else {
              const brandMatch = url.pathname.match(/^\/brands\/([^/]+)$/);
              if (brandMatch?.[1]) {
                resetBrandState(brandMatch[1]);
              }
            }
          } catch (error) {
            // Silently handle error
          }
          break;
        }
        target = target.parentElement;
      }
    };

    document.addEventListener('click', handleDocumentClick, true);
    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, []);

  const resetHomeState = () => {
    try {
      sessionStorage.removeItem('home_filter_state');
      const recommendationKeys = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('recommendation_cards_')) {
          recommendationKeys.push(key);
        }
      }
      recommendationKeys.forEach((key) => sessionStorage.removeItem(key));
    } catch (error) {
      // Silently handle error
    }
  };

  const resetBrandState = (brandId) => {
    if (!brandId) return;
    try {
      sessionStorage.removeItem(`brand_page_filters_${brandId}`);
    } catch (error) {
      // Silently handle error
    }
  };

  const resetListingsState = () => {
    try {
      sessionStorage.removeItem('listings_page_state');
    } catch (error) {
      // Silently handle error
    }
  };

  const resetFavoritesState = () => {
    try {
      // Clear favorites page pagination / data cache
      sessionStorage.removeItem('favorites_page_state');

      // Also clear scroll position cache for favorites page to avoid jumping back to an old anchor
      const scrollKeysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('scroll_/me/favorites')) {
          scrollKeysToRemove.push(key);
        }
      }
      scrollKeysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch (error) {
      // Silently handle error
    }
  };

  const resetOrdersState = () => {
    try {
      // Clear all orders page state keys
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('orders_page_state')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));

      // Also clear scroll position cache for orders page to avoid jumping back to an old anchor
      const scrollKeysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('scroll_/me/orders')) {
          scrollKeysToRemove.push(key);
        }
      }
      scrollKeysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch (error) {
      // Silently handle error
    }
  };

  // Listen to path changes
  useEffect(() => {
    const currentPath = location.pathname;
    const currentKey = location.key;

    const hasPathChanged = currentPath !== previousPath.current;
    const hasKeyChanged = currentKey !== previousLocationKey.current;

    // Path changed OR same path but new navigation entry (location key changed)
    if (hasPathChanged || hasKeyChanged) {
      // If not back navigation (i.e., active link click), invalidate cache
      if (!isPopStateNavigation.current) {
        // Invalidate home page data
        if (currentPath === '/') {
          queryClient.invalidateQueries({ queryKey: ['cards'] });
          queryClient.invalidateQueries({ queryKey: ['heroBanners'] });
          queryClient.invalidateQueries({ queryKey: ['featuredProducts'] });
          resetHomeState();
        }
        
        // Invalidate search page data
        if (currentPath.startsWith('/search')) {
          queryClient.invalidateQueries({ queryKey: ['searchCards'] });
        }
        
        // Invalidate brand page data
        if (currentPath.startsWith('/brands/')) {
          queryClient.invalidateQueries({ queryKey: ['brand-cards'] });
          queryClient.invalidateQueries({ queryKey: ['brands'] });

          const brandMatch = currentPath.match(/^\/brands\/([^/]+)/);
          if (brandMatch?.[1]) {
            resetBrandState(brandMatch[1]);
          }
        }

        if (currentPath === '/me/listings') {
          resetListingsState();
        }

        // Active navigation to favorites page: reset favorites cache and refetch data as needed
        if (currentPath === '/me/favorites') {
          resetFavoritesState();
          queryClient.invalidateQueries({ queryKey: ['watches'] });
        }

        // Active navigation to orders page: reset orders cache and refetch data as needed
        if (currentPath === '/me/orders') {
          resetOrdersState();
          queryClient.invalidateQueries({ queryKey: ['purchases'] });
        }
        
        // Invalidate card detail page data (only for exact /card/:id route)
        const cardDetailMatch = currentPath.match(/^\/card\/([^/]+)$/);
        if (cardDetailMatch) {
          const cardId = cardDetailMatch[1];
          // Remove cached queries related to this card to force a full refresh of detail data and loading state
          queryClient.removeQueries({ queryKey: ['card', cardId], exact: true });
          queryClient.removeQueries({ queryKey: ['cardStats', cardId], exact: true });
          queryClient.removeQueries({ queryKey: ['recentPurchases', cardId], exact: true });
          queryClient.removeQueries({ queryKey: ['cardFamily', cardId], exact: true });
          queryClient.removeQueries({ queryKey: ['relatedCards', cardId], exact: true });
        }
      }

      // Reset flag
      setTimeout(() => {
        isPopStateNavigation.current = false;
      }, 100);
    }

    previousPath.current = currentPath;
    previousLocationKey.current = currentKey;
  }, [location.pathname, location.key, queryClient]);

  return null;
};

export default NavigationHandler;

